'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { processHeading } from '@workspace/lib/heading-utils';
import { getBackgroundClass } from '@workspace/lib/helper';
import type { UseGalleryHookResult, UseGalleryParams } from './gallery.types';
import { SanityImage } from '@workspace/interfaces';

export const useGallery = ({
  images,
  isOpen,
  startIndex = 0,
  onRequestClose,
  heading,
  backgroundColor,
}: UseGalleryParams & {
  heading?: any;
  backgroundColor?: any;
}): UseGalleryHookResult => {
  const [isGalleryOpen, setIsGalleryOpen] = useState<boolean>(!!isOpen);
  const [selectedIndex, setSelectedIndex] = useState<number>(startIndex);
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);

  const gallerySlides: SanityImage[] = useMemo(() => {
    if (!images) return [];
    // Keep only images with a usable ref or url to avoid empty slides
    return images.filter((img): img is SanityImage =>
      Boolean(img && (img.asset?._ref || img.asset?._id || img.asset?.url || img.url)),
    );
  }, [images]);

  const [{ text: headingText }] = useMemo(() => [processHeading(heading)], [heading]);
  const backgroundClass = useMemo(() => getBackgroundClass(backgroundColor), [backgroundColor]);

  // Embla carousel for infinite thumbnail scrolling
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
  });

  const handleImageClick = useCallback((index: number) => {
    setSelectedIndex(index);
    setIsGalleryOpen(true);
  }, []);

  const updateScrollButtons = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollLeft(emblaApi.canScrollPrev());
    setCanScrollRight(emblaApi.canScrollNext());
  }, [emblaApi]);

  const scrollThumbnails = useCallback(
    (direction: 'left' | 'right') => {
      if (!emblaApi) return;
      if (direction === 'left') {
        emblaApi.scrollPrev();
      } else {
        emblaApi.scrollNext();
      }
    },
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;
    updateScrollButtons();
    emblaApi.on('select', updateScrollButtons);
    emblaApi.on('reInit', updateScrollButtons);
    return () => {
      emblaApi.off('select', updateScrollButtons);
      emblaApi.off('reInit', updateScrollButtons);
    };
  }, [emblaApi, updateScrollButtons]);

  const getVisibleThumbnailIndices = useCallback((length: number, selected: number, windowSize = 3): number[] => {
    if (length <= windowSize) return Array.from({ length }, (_, i) => i);
    let start = selected - Math.floor(windowSize / 2);
    if (start < 0) start = 0;
    if (start > length - windowSize) start = length - windowSize;
    return Array.from({ length: windowSize }, (_, k) => start + k);
  }, []);

  const visibleThumbIndices = useMemo(() => {
    return getVisibleThumbnailIndices(gallerySlides.length, selectedIndex, 3);
  }, [gallerySlides.length, selectedIndex, getVisibleThumbnailIndices]);

  const onSlide = useCallback((i: number) => setSelectedIndex(i), []);

  const closeGallery = useCallback(() => {
    setIsGalleryOpen(false);
    onRequestClose && onRequestClose();
  }, [onRequestClose]);

  // Combined effect for external state updates to reduce effect count
  useEffect(() => {
    if (typeof isOpen === 'boolean') {
      setIsGalleryOpen(isOpen);
    }
    setSelectedIndex(startIndex);
  }, [isOpen, startIndex]);

  return {
    isGalleryOpen,
    selectedIndex,
    canScrollLeft,
    canScrollRight,
    gallerySlides,
    emblaRef,
    visibleThumbIndices,
    handleImageClick,
    scrollThumbnails,
    onSlide,
    closeGallery,
    images: gallerySlides,
    headingText,
    backgroundClass,
  };
};
